import React, { memo, useEffect } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useProjectState } from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIsPremiumUserSeat } from '@atlassian/jira-router-resources-business-license-details/src/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { useSidebarData } from '../../controllers/sidebar-data/index.tsx';
import { CreateProjectCta } from './create-project-cta/index.tsx';
import FeedbackButton from './feedback-button/index.tsx';
import messages from './messages.tsx';
import Overviews from './overviews/index.tsx';
import ProjectGroup from './project-group/index.tsx';
import ProjectsHeader from './projects-header/index.tsx';

const MultiProjectMenu = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		currentProject,
		recents,
		favorites,
		hasMoreFavorite,
		onFavoriteToggle,
		overviews,
		overviewsError,
		shouldHideOverviews,
		loading,
	} = useSidebarData();
	const isAnonymous = useIsAnonymous();
	const isPremiumUserSeat = useIsPremiumUserSeat();
	const [projectState] = useProjectState();

	const [
		{
			match: {
				params: { projectKey },
			},
		},
	] = useRouter();

	useEffect(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'sidebar viewed', {
			starredProjectsCount: favorites.length,
			recentProjectsCount: recents.length,
			overviewsCount: overviews.length,
		});
		// only run once on component mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// the currentProject data gets loaded async and thus it is null while
	// transitioning between projects. To allow expanding the right current
	// project when the data has not been resolved yet, we use the project
	// key from the URL instead.
	const currentProjectKey = projectKey ?? currentProject?.key;

	const shouldDisplayOverviews = isPremiumUserSeat && !isAnonymous && !shouldHideOverviews;

	const projectsContent = (
		<section>
			{favorites.length > 0 && (
				<ProjectGroup
					sectionId="starred"
					projects={favorites}
					onFavoriteToggle={onFavoriteToggle}
					currentProjectKey={currentProjectKey}
					showViewAllProjects={hasMoreFavorite}
					viewAllProjectsLabel={formatMessage(messages.viewAllStarred)}
				/>
			)}

			<ProjectGroup
				sectionId="recent"
				projects={recents}
				onFavoriteToggle={onFavoriteToggle}
				currentProjectKey={currentProjectKey}
				showViewAllProjects
			/>
		</section>
	);

	const isShowingCTA = projectState?.isSampleProject === true;

	return (
		<Container>
			<SidebarWrapper aria-label={formatMessage(messages.navigationLabel)} isCrossProjectView>
				{shouldDisplayOverviews &&
					(loading ? (
						<OverviewsSkeleton />
					) : (
						<section data-testid="navigation-apps-sidebar-core.ui.multi-project-menu.overviews">
							<Overviews overviews={overviews} hasError={Boolean(overviewsError)} />
						</section>
					))}
				{loading ? (
					<ProjectsSkeleton />
				) : (
					<section data-testid="navigation-apps-sidebar-core.ui.multi-project-menu.projects">
						<ProjectsHeader />
						{projectsContent}
					</section>
				)}
				{!loading && isShowingCTA && <CreateProjectCta />}
			</SidebarWrapper>
			<Footer hasMargin>
				{currentProject != null && (
					<Type data-testid="navigation-apps-sidebar-core.ui.multi-project-menu.project-type">
						{currentProject.isSimplified
							? formatMessage(messages.teamManagedProject)
							: formatMessage(messages.companyManagedProject)}
					</Type>
				)}
				<FeedbackButton />
			</Footer>
		</Container>
	);
};

const ItemSkeleton = () => (
	<ItemSkeletonWrapper>
		<ItemAvatarSkeleton />
		<SkeletonBase />
	</ItemSkeletonWrapper>
);

const OverviewsSkeleton = () => (
	<SkeletonSection>
		<SectionHeaderSkeleton />
		<ItemSkeleton />
		<ItemSkeleton />
	</SkeletonSection>
);

const ProjectsSkeleton = () => (
	<SkeletonSection>
		<SectionHeaderSkeleton />
		<CollapsibleHeaderSkeleton />
		<ItemSkeleton />
		<ItemSkeleton />
		<CollapsibleHeaderSkeleton />
		<ItemSkeleton />
		<ItemSkeleton />
		<ItemSkeleton />
		<ItemSkeleton />
	</SkeletonSection>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	height: '100%',
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',

	backgroundColor: token('elevation.surface'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SidebarWrapper = styled.nav<{ isCrossProjectView?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingTop: ({ isCrossProjectView }) =>
		isCrossProjectView ? token('space.200') : token('space.050'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
	paddingRight: token('space.200'),
	marginTop: token('space.negative.050'),
	marginBottom: 0,
	marginLeft: 0,
	marginRight: 0,
	overflow: 'auto',
	flex: 1,

	background: `linear-gradient(${token('elevation.surface')}, ${token('elevation.surface')}) 0 100%,
${''}
        linear-gradient(${token('color.border')}, ${token('color.border')}) 0 100%`,
	backgroundRepeat: 'no-repeat',
	backgroundSize: '100% 2px, 100% 2px',
	backgroundAttachment: 'local, scroll',
	backgroundOrigin: 'border-box, border-box',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'section + section': {
		marginTop: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonBase = styled.div({
	backgroundColor: token('color.background.neutral'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 1.5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 18}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize / 2}px`,
	marginBottom: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonSection = styled.div({
	marginTop: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionHeaderSkeleton = styled(SkeletonBase)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 15}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 2}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemSkeletonWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ItemAvatarSkeleton = styled(SkeletonBase)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 3}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 3}px`,
	marginLeft: token('space.150'),
	marginRight: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CollapsibleHeaderSkeleton = styled(SkeletonBase)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 10}px`,
	marginLeft: token('space.100'),
	marginTop: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Footer = styled.div<{ hasMargin?: boolean }>(
	{
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.hasMargin && {
			margin: token('space.100'),
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Type = styled.div({
	font: token('font.body.small'),
	textAlign: 'center',
	marginTop: token('space.050'),
	marginRight: 0,
	marginBottom: token('space.050'),
	marginLeft: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${2 * gridSize}px`,

	color: token('color.text.subtlest'),
});

const MemoMultiProjectMenu: React.MemoExoticComponent<() => React.JSX.Element> =
	memo(MultiProjectMenu);
export default MemoMultiProjectMenu;
